import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ChevronRight, Droplets, DollarSign, Zap, Phone, ArrowRight, Flame, Gauge } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

const TOOLS = [
  {
    slug: "leak-test",
    path: "/tools/leak-test",
    title: "15-Minute Leak Test",
    tag: "Diagnose",
    desc: "Use your water meter to catch a hidden leak before it costs you thousands. No tools needed.",
    icon: Droplets,
    ready: true,
    status: "LIVE",
  },
  {
    slug: "rebate-finder",
    path: "/tools/rebate-finder",
    title: "Hot Water Rebate Finder",
    tag: "Save",
    desc: "Victorian rebates for upgrading to heat pump or solar hot water. Rough estimate in 30 seconds.",
    icon: Zap,
    ready: true,
    status: "LIVE",
  },
  {
    slug: "pricing",
    path: "/pricing",
    title: "Pricing Guide",
    tag: "Transparency",
    desc: "Rough price bands for common plumbing jobs — before you ring for a quote.",
    icon: DollarSign,
    ready: true,
    status: "LIVE",
  },
  {
    slug: "water-pressure",
    path: "/tools/pressure-test",
    title: "Water Pressure Test",
    tag: "Diagnose",
    desc: "Quick bucket-method check — tells you if your mains pressure is healthy, low, or too high.",
    icon: Gauge,
    ready: true,
    status: "LIVE",
  },
  {
    slug: "hw-repair-replace",
    path: "/tools/hot-water-decision",
    title: "Hot Water: Repair or Replace?",
    tag: "Decide",
    desc: "Four questions in, honest call out: should you fix the old one or swap it out.",
    icon: Flame,
    ready: true,
    status: "LIVE",
  },
];

const Tools = () => (
  <div data-testid="tools-page">
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
      <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-20">
        <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
          <Link to="/" className="text-muted hover:text-gold">Home</Link>
          <ChevronRight size={12} className="text-dim" />
          <span className="text-ink">Free Tools</span>
        </nav>
        <div className="max-w-3xl">
          <div className="eyebrow mb-6"><span>Free Tools</span></div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
          >
            Tools that
            <br />
            <span className="text-gold">actually help.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed"
          >
            A plumber&apos;s job is to fix things. Our job here is to help you figure out
            whether you need one in the first place — and if you do, what to expect.
          </motion.p>
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-page px-6 lg:px-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {TOOLS.map((t, i) => {
          const Card = t.ready ? Link : "div";
          const extra = t.ready ? { to: t.path } : {};
          return (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
            >
              <Card
                {...extra}
                data-testid={`tool-card-${t.slug}`}
                className={`block bg-card border border-line p-8 h-full transition-all ${
                  t.ready ? "hover:border-gold/40 hover:bg-cardHover cursor-pointer" : "opacity-70 cursor-default"
                }`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 border border-gold/30 bg-gold/5 flex items-center justify-center">
                    <t.icon size={20} className="text-gold" />
                  </div>
                  <span className={`text-[9px] tracking-widest border px-2 py-1 ${
                    t.ready ? "border-gold/40 text-gold bg-gold/5" : "border-line text-muted"
                  }`}>
                    {t.status}
                  </span>
                </div>
                <div className="text-gold text-[10px] tracking-widerx uppercase mb-2">{t.tag}</div>
                <h3 className="font-display text-2xl md:text-3xl text-ink mb-3 leading-tight">{t.title}</h3>
                <p className="text-muted text-sm leading-relaxed">{t.desc}</p>
                {t.ready && (
                  <div className="mt-6 inline-flex items-center gap-2 text-gold text-xs tracking-widerx uppercase">
                    Open <ArrowRight size={12} />
                  </div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>
    </section>

    <section className="py-20 lg:py-28 bg-elevated border-t border-line">
      <div className="mx-auto max-w-page px-6 lg:px-14 text-center">
        <h2 className="font-display text-5xl md:text-7xl text-ink mb-6">
          Still unsure? <span className="text-gold">We'll walk you through it.</span>
        </h2>
        <p className="text-muted text-lg max-w-xl mx-auto mb-10">
          Our chatbot answers plumbing questions 24/7. For anything serious, the phone line is answered live.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a href={PHONE_HREF} className="btn-gold" data-testid="tools-cta-call">
            <Phone size={16} /> Call {PHONE}
          </a>
          <Link to="/contact" className="btn-ghost">Request a Quote <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  </div>
);

export default Tools;
